'use client';
import React from 'react';
import { notFound } from 'next/navigation';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import { productData } from './productData';
import type { NutritionRow } from './productData';
import { fireFloodlightPixel, FLOODLIGHT_ACTIVITIES } from '@/lib/floodlight';
import { useFloodlightPageview } from '@/lib/useFloodlightPageview';

const allProductSlugs = Object.keys(productData);

/* ─── SECTION HEADER ─── */
function SectionHeading({ pre, accent, children }: { pre: string; accent: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <span className="block text-[10px] uppercase tracking-[0.25em] font-semibold mb-2" style={{ color: accent }}>
        {pre}
      </span>
      <h2 className="font-display text-2xl md:text-[1.75rem] font-[200] leading-tight" style={{ color: 'var(--foreground)', letterSpacing: '0.02em' }}>
        {children}
      </h2>
    </div>
  );
}

/* ─── NUTRITION TABLE ─── */
function NutritionTable({ rows, accent, bgAccent }: { rows: NutritionRow[]; accent: string; bgAccent: string }) {
  return (
    <div className="rounded-[1.75rem] overflow-hidden" style={{ border: '1px solid var(--border)' }}>
      {/* Header — mirrors the body row's column structure exactly so labels line up with data */}
      <div className="flex items-center justify-between px-6 py-4" style={{ background: `linear-gradient(135deg, ${bgAccent} 0%, ${bgAccent}CC 100%)` }}>
        <h3 className="font-display text-lg font-[200] leading-tight" style={{ color: '#FDF8F2' }}>
          Nutrition Facts
        </h3>
        <div className="flex items-center gap-4 sm:gap-10 text-right shrink-0">
          <span className="text-[9px] font-bold uppercase tracking-wider leading-tight" style={{ color: 'rgba(253,248,242,0.85)', minWidth: '64px' }}>
            Per 100g
          </span>
          <span className="text-[9px] font-bold uppercase tracking-wider leading-tight" style={{ color: 'rgba(253,248,242,0.85)', minWidth: '48px' }}>
            %DV*
          </span>
        </div>
      </div>
      {/* Rows */}
      <div style={{ background: 'var(--card)' }}>
        {rows.map((row, i) => {
          const isHighlight = ['Protein', 'Dietary Fibre', 'Added Sugars', 'Iron', 'Calcium', 'Vitamin B12', 'Folic Acid'].includes(row.label);
          return (
            <div
              key={row.label}
              className="flex items-center justify-between px-6 py-3.5"
              style={{
                borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none',
                background: isHighlight ? `${accent}08` : 'transparent',
              }}
            >
              <span
                className="text-sm"
                style={{
                  color: 'var(--foreground)',
                  fontWeight: isHighlight ? 600 : 400,
                }}
              >
                {row.label}
              </span>
              <div className="flex items-center gap-4 sm:gap-10 text-right shrink-0">
                <span className="text-base font-semibold tabular-nums" style={{ color: accent, minWidth: '64px' }}>
                  {row.per100g}
                </span>
                <span
                  className="text-[11px] font-bold tabular-nums rounded-full px-2 py-0.5"
                  style={{
                    background: row.dv ? `${accent}18` : 'transparent',
                    color: row.dv ? accent : 'transparent',
                    minWidth: '48px',
                    textAlign: 'center',
                  }}
                >
                  {row.dv || '—'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-6 py-3 text-[11px]" style={{ background: 'var(--secondary)', color: 'var(--muted-foreground)' }}>
        *% Daily Values based on a 2000 kcal diet.
      </div>
    </div>
  );
}

export default function ProductDetailClient({ slug }: { slug: string }) {
  useFloodlightPageview(FLOODLIGHT_ACTIVITIES.productStandalonePage);

  const product = productData[slug];

  if (!product) {
    notFound();
  }

  const otherProducts = allProductSlugs
    .filter((s) => s !== slug)
    .slice(0, 3)
    .map((s) => productData[s]);

  return (
    <main className="grain-overlay bg-background min-h-screen overflow-x-hidden">
      <Header />

      {/* ─── HERO ─── */}
      <section className="pt-24 md:pt-28 pb-8 md:pb-10" style={{ background: 'var(--background)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-6 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Link href="/" className="hover:opacity-70 transition-opacity">Home</Link>
            <span>/</span>
            <Link href="/#products" className="hover:opacity-70 transition-opacity">Products</Link>
            <span>/</span>
            <span style={{ color: 'var(--foreground)' }}>{product.name}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 items-center">
            {/* Image first on mobile — standard e-commerce order (see the product before reading about it) */}
            <div
              className="order-1 md:order-1 relative rounded-3xl overflow-hidden mx-auto md:mx-0 w-full"
              style={{
                aspectRatio: '1 / 1',
                maxWidth: '420px',
                border: '1px solid var(--border)',
                boxShadow: '0 20px 48px -16px rgba(0,0,0,0.18)',
              }}
            >
              <AppImage
                src={product.image}
                alt={product.alt}
                fill
                sizes="(max-width: 768px) 80vw, 420px"
                className="object-cover w-full h-full"
                priority
              />
            </div>

            {/* Copy + buy box */}
            <div className="order-2 md:order-2">
              {product.badge && (
                <span
                  className="inline-block text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full mb-4"
                  style={{ background: 'var(--secondary)', color: 'var(--accent-ink)', border: '1px solid var(--border)' }}
                >
                  {product.badge}
                </span>
              )}
              <h1
                className="font-display leading-[1.02] mb-2"
                style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 200, letterSpacing: '0.02em', color: 'var(--foreground)' }}
              >
                {product.name}
              </h1>
              <p className="text-sm font-semibold mb-3" style={{ color: 'var(--accent-ink)' }}>
                {product.tagline}
              </p>
              <p className="text-sm leading-relaxed mb-5 max-w-md" style={{ color: 'var(--muted-foreground)' }}>
                {product.description}
              </p>
              {/* Highlight pills */}
              <div className="flex flex-wrap gap-2 mb-6">
                {product.highlights.slice(0, 4).map((h) => (
                  <span
                    key={h}
                    className="text-[10px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full"
                    style={{ background: 'var(--secondary)', color: 'var(--foreground)', border: '1px solid var(--border)' }}
                  >
                    {h}
                  </span>
                ))}
              </div>
              {/* Buy buttons — the actual purchase links, visible without scrolling */}
              <div className="flex flex-wrap gap-2.5 mb-3">
                {product.purchaseLinks.map((p) => (
                  <a
                    key={p.platform}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      if (p.platform === 'Amazon') fireFloodlightPixel(FLOODLIGHT_ACTIVITIES.pdpBuyOnAmazonButton);
                      else if (p.platform === 'Flipkart') fireFloodlightPixel(FLOODLIGHT_ACTIVITIES.pdpBuyOnFlipkartButton);
                    }}
                    className="group inline-flex items-center gap-2 pl-2 pr-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 hover:-translate-y-0.5"
                    style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                  >
                    <span className="w-7 h-7 rounded-lg bg-white flex items-center justify-center shrink-0 overflow-hidden">
                      <AppImage src={p.logo} alt={p.platform} width={20} height={20} className="object-contain w-full h-full p-0.5" />
                    </span>
                    Buy on {p.platform}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── MAIN CONTENT ─── */}
      <section className="pt-8 pb-16 md:pb-20 border-t" style={{ background: 'var(--background)', borderColor: 'var(--border)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12">

          {/* ── NUTRITION ── */}
          <div className="mb-14">
            <SectionHeading pre="Nutrition" accent={product.textAccent}>
              What&apos;s in every 100g.
            </SectionHeading>
            <NutritionTable rows={product.nutritionFacts} accent={product.textAccent} bgAccent={product.accent} />
          </div>

          <div className="flex flex-col gap-14">

            {/* ── INGREDIENTS ── */}
            <div>
              <SectionHeading pre="Ingredients" accent={product.textAccent}>
                What&apos;s inside &amp; why it matters.
              </SectionHeading>
              <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--muted-foreground)' }}>
                {product.longDescription}
              </p>

              {/* Full ingredients declaration */}
              <div
                className="p-5 rounded-2xl mb-6"
                style={{ background: 'var(--card)', border: '1px solid var(--border)', borderLeft: `3px solid ${product.textAccent}` }}
              >
                <p className="text-[10px] uppercase tracking-widest font-semibold mb-2" style={{ color: 'var(--muted-foreground)' }}>
                  Full Declaration
                </p>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--foreground)' }}>
                  {product.ingredientsList}
                </p>
              </div>

              {/* Ingredient cards */}
              {product.ingredients.length > 1 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {product.ingredients.map((ing) => (
                    <div
                      key={ing.name}
                      className="flex items-start gap-4 p-4 rounded-xl transition-all duration-200 hover:-translate-y-0.5"
                      style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                    >
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-lg"
                        style={{ background: `${product.textAccent}18` }}
                      >
                        <span>{ing.icon}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{ing.name}</p>
                        <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{ing.benefit}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── PROCESS BREAK ── */}
            <div className="relative rounded-[1.75rem] overflow-hidden" style={{ height: '280px' }}>
              <AppImage
                src="/assets/images/kasturi_hero_2_flour.png"
                alt="Stone-milled grains at the heart of every Kasturi product"
                fill
                className="object-cover"
              />
              <div
                className="absolute inset-0"
                style={{ background: `linear-gradient(105deg, ${product.accent}E6 0%, ${product.accent}80 45%, transparent 80%)` }}
              />
              <div className="absolute inset-0 flex items-center">
                <p
                  className="font-display italic leading-tight px-8 max-w-sm"
                  style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', color: '#FDF8F2' }}
                >
                  &ldquo;A daily essential for your busy life.&rdquo;
                  <span className="block not-italic text-xs font-sans uppercase tracking-widest mt-3" style={{ color: `${product.textAccent}CC` }}>
                    — Kasturi Food Products
                  </span>
                </p>
              </div>
            </div>

            {/* ── CERTIFICATIONS ── */}
            <div>
              <SectionHeading pre="Certifications & Claims" accent={product.textAccent}>
                Trust &amp; transparency.
              </SectionHeading>
              <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--muted-foreground)' }}>
                Every Kasturi product is manufactured with full regulatory compliance and transparent labelling. No hidden additives, no misleading claims.
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {product.certifications.map((cert) => (
                  <div
                    key={cert.name}
                    className="flex flex-col items-center text-center gap-3 p-5 rounded-2xl transition-all duration-200 hover:-translate-y-0.5"
                    style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                  >
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: `${product.textAccent}18` }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={product.textAccent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-bold text-xs leading-tight" style={{ color: 'var(--foreground)' }}>{cert.name}</p>
                      <p className="text-[11px] mt-1.5 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{cert.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Back link */}
            <Link
              href="/#products"
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider transition-all duration-200 hover:gap-3 self-start"
              style={{ color: 'var(--muted-foreground)' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 5l-7 7 7 7" />
              </svg>
              All Products
            </Link>

          </div>
        </div>
      </section>

      {/* ─── MORE FROM KASTURI ─── */}
      <section className="py-16 border-t" style={{ borderColor: 'var(--border)', background: 'var(--secondary)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 mb-10">
            <div className="h-px w-8" style={{ background: 'var(--primary)' }} />
            <span className="text-[10px] uppercase tracking-[0.3em]" style={{ color: 'var(--accent-ink)' }}>More from Kasturi</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {otherProducts.map((p) => (
              <Link
                key={p.id}
                href={`/products/${p.id}`}
                className="group flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 hover:-translate-y-1"
                style={{ borderColor: 'var(--border)', background: 'var(--card)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
              >
                <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0" style={{ background: p.accent }}>
                  <AppImage src={p.image} alt={p.alt} width={64} height={64} className="object-contain w-full h-full p-1" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm leading-tight" style={{ color: 'var(--foreground)' }}>{p.name}</p>
                  <p className="text-xs mt-1 truncate" style={{ color: 'var(--muted-foreground)' }}>{p.tagline}</p>
                </div>
                <svg
                  className="shrink-0 transition-transform duration-300 group-hover:translate-x-1"
                  width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ color: 'var(--accent-ink)' }}
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
